import React from "react";
import { Box, Checkbox, Typography } from "@mui/material";
import { Serializer, QuestionCheckboxModel, SurveyModel } from "survey-core";
import {
  ReactQuestionFactory,
  SurveyQuestionElementBase
} from "survey-react-ui";
import GLButton from "components/v3/sharedComponents/GLButton";
import { COLORS } from "components/v3/Theme/colors";
import { ArrowForward } from "@mui/icons-material";

class QuestionCheckboxWithButtonModel extends QuestionCheckboxModel {
  getType() {
    return "checkbox-with-button";
  }

  get completeButtonText(): string {
    return this.getPropertyValue("completeButtonText") || "Continue";
  }

  set completeButtonText(value: string) {
    this.setPropertyValue("completeButtonText", value);
  }

  get cancelButtonText(): string {
    return this.getPropertyValue("cancelButtonText") || "";
  }

  set cancelButtonText(value: string) {
    this.setPropertyValue("cancelButtonText", value);
  }
}

Serializer.addClass(
  "checkbox-with-button",
  [
    { name: "completeButtonText", default: "Continue" },
    { name: "cancelButtonText", default: "" }
  ],
  () => new QuestionCheckboxWithButtonModel(""),
  "checkbox"
);

interface CheckboxWithButtonState {
  canComplete: boolean;
}

class SurveyQuestionCheckboxWithButton extends SurveyQuestionElementBase {
  state: CheckboxWithButtonState = { canComplete: false };
  private valueChangedHandler: () => void;
  private pageChangedHandler: () => void;

  constructor(props: any) {
    super(props);
    this.valueChangedHandler = () => this.updateCanComplete();
    this.pageChangedHandler = () => this.updateCanComplete();
  }

  private get questionModel(): QuestionCheckboxWithButtonModel {
    return (
      (this.questionBase as QuestionCheckboxWithButtonModel) ||
      ((this.props as any).element as QuestionCheckboxWithButtonModel)
    );
  }

  private get survey(): SurveyModel {
    return this.questionModel.survey as SurveyModel;
  }

  componentDidMount() {
    super.componentDidMount();
    const survey = this.survey;
    if (survey) {
      survey.onValueChanged.add(this.valueChangedHandler);
      survey.onCurrentPageChanged.add(this.pageChangedHandler);
    }
    this.updateCanComplete();
  }

  componentWillUnmount() {
    const survey = this.survey;
    if (survey) {
      survey.onValueChanged.remove(this.valueChangedHandler);
      survey.onCurrentPageChanged.remove(this.pageChangedHandler);
    }
    super.componentWillUnmount();
  }

  private allChoicesSelected(): boolean {
    const question = this.questionModel;
    const selected = Array.isArray(question.value) ? question.value : [];
    return question.choices.every((choice) => selected.includes(choice.value));
  }

  private updateCanComplete() {
    const survey = this.survey;
    if (!survey) {
      return;
    }
    const canComplete =
      survey.isLastPage &&
      this.allChoicesSelected() &&
      !survey.hasErrors(false, false);
    if (this.state.canComplete !== canComplete) {
      this.setState({ canComplete });
    }
  }

  private handleChange =
    (choiceValue: string) =>
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const question = this.questionModel;
      const current = Array.isArray(question.value) ? [...question.value] : [];
      if (event.target.checked) {
        if (!current.includes(choiceValue)) {
          question.value = [...current, choiceValue];
        }
      } else {
        question.value = current.filter((value) => value !== choiceValue);
      }
    };

  private handleSubmit = () => {
    const survey = this.survey;
    if (survey && this.allChoicesSelected() && survey.validate()) {
      survey.tryComplete();
    }
  };

  private handleCancel = () => {
    const survey = this.survey as SurveyModel & {
      onFormCancel?: () => void;
    };
    survey?.onFormCancel?.();
  };

  protected renderElement(): React.ReactElement {
    const question = this.questionModel;
    const selected = Array.isArray(question.value) ? question.value : [];
    const buttonDisabled = !this.state.canComplete;
    const cancelLabel = question.cancelButtonText;
    const showCancel = Boolean(cancelLabel);
    const splitLayout = showCancel;

    return (
      <Box
        className="terms-checkbox-with-button"
        sx={{
          alignItems: splitLayout
            ? { sm: "flex-end", xs: "stretch" }
            : "stretch",
          display: "flex",
          flexDirection: splitLayout
            ? { sm: "row", xs: "column" }
            : "column",
          flexShrink: 0,
          gap: splitLayout ? { sm: 3, xs: 3 } : 4,
          justifyContent: splitLayout ? "space-between" : "flex-start",
          width: "100%"
        }}
      >
        <Box
          sx={{
            display: "flex",
            flex: "1 1 auto",
            flexDirection: "column",
            gap: 1.5,
            minWidth: 0
          }}
        >
          {question.choices.map((choice) => {
            const checked = selected.includes(choice.value);
            return (
              <Box
                key={String(choice.value)}
                sx={{
                  alignItems: "center",
                  display: "flex",
                  flex: "0 0 auto",
                  gap: 1.5,
                  minWidth: 0,
                  width: "100%"
                }}
              >
                <Checkbox
                  checked={checked}
                  onChange={this.handleChange(choice.value)}
                  sx={{
                    flexShrink: 0,
                    p: 0.5,
                    "& .MuiSvgIcon-root": {
                      fontSize: 22
                    }
                  }}
                />
                <Typography
                  sx={{
                    color: COLORS.BLACK,
                    flex: 1,
                    fontWeight: 600,
                    lineHeight: 1.4,
                    minWidth: 0,
                    overflowWrap: "break-word",
                    whiteSpace: "normal"
                  }}
                >
                  {choice.text || question.title}
                </Typography>
              </Box>
            );
          })}
        </Box>
        <Box
          sx={{
            alignItems: "center",
            alignSelf: splitLayout
              ? { sm: "flex-end", xs: "stretch" }
              : "flex-start",
            display: "flex",
            flexDirection: { sm: "row", xs: "column-reverse" },
            flexShrink: 0,
            gap: 2,
            justifyContent: splitLayout
              ? { sm: "flex-end", xs: "stretch" }
              : "flex-start"
          }}
        >
          {showCancel ? (
            <GLButton
              buttonType="secondary-no-border"
              onClick={this.handleCancel}
              sx={{
                color: COLORS.DARK_GRAY,
                typography: {
                  xs: "body_strong_dark_gray",
                  md: "body_strong_lg_dark_gray"
                },
                "&:hover:not(.Mui-disabled)": {
                  backgroundColor: "transparent",
                  color: COLORS.BLACK
                }
              }}
              text={cancelLabel}
            />
          ) : null}
          <GLButton
            text={question.completeButtonText}
            disabled={buttonDisabled}
            onClick={this.handleSubmit}
            rightIcon={<ArrowForward />}
          />
        </Box>
      </Box>
    );
  }
}

ReactQuestionFactory.Instance.registerQuestion(
  "checkbox-with-button",
  (props) => {
    return React.createElement(SurveyQuestionCheckboxWithButton, props);
  }
);

export default SurveyQuestionCheckboxWithButton;
